import React, { useEffect, useRef, useState } from 'react';
import './scoreFilters.css';
import { apiFetch } from './edgePayloads.js';

// Price range for the filter drawer: a bar per price step showing how many homes are for sale at
// that price, with two handles like the area-score bars. The bars follow the search and every other
// filter in the drawer as it is being changed (not the price itself): the view passes
// getPriceHistogram(filters, edges) (kartvy counts its loaded listings, listvy asks the server for its
// search); without one, or when it returns null, the server counts all of Sweden. The steps are the ones
// the price dropdowns used, so the saved values (priceMin 'min' | kr, priceMax 'max' | kr) keep their meaning.
const API_BASE = String(process.env.REACT_APP_API_BASE_URL || 'https://api.xn--omrdeskollen-vcb.nu').replace(/\/+$/, '');

export const PRICE_EDGES = [
    50000, 100000, 200000, 300000, 400000, 500000,
    1000000, 1500000, 2000000, 2500000, 3000000, 3500000, 4000000, 4500000, 5000000, 5500000,
    6000000, 6500000, 7000000, 7500000, 8000000, 8500000, 9000000, 9500000, 10000000,
    11000000, 12000000, 13000000, 14000000, 15000000, 16000000, 17000000, 18000000, 19000000, 20000000,
    25000000, 30000000,
];
// Monthly fee steps (FeeRangeBar): 500 kr up to 10 000, then 1 000 up to 15 000. Saved feeMax values from
// the old dropdown (any multiple of 500) round up to the next step.
export const FEE_EDGES = [
    ...Array.from({ length: 20 }, (_, index) => (index + 1) * 500),
    11000, 12000, 13000, 14000, 15000,
];

const histogramCache = new Map();
const HISTOGRAM_CACHE_MAX = 60;
const RECOUNT_DELAY_MS = 250;

// The drawer's filters as the histogram request takes them: every filter but the counted field's own
// (field 'price': price left out; 'fee': feeMax left out, price kept).
export const histogramFilterBody = (filters = {}, field = 'price') => {
    const body = {};
    if (filters.houseType && filters.houseType !== 'all') body.houseType = filters.houseType;
    const bounds = [['roomMin', 'min'], ['roomMax', 'max'], ['sqmMin', 'min'], ['sqmMax', 'max'],
        ['sqmPriceMin', 'min'], ['sqmPriceMax', 'max'], ['floorMin', 'min'], ['floorMax', 'max'],
        ['yearBuiltMin', 'min'], ['yearBuiltMax', 'max']];
    if (field === 'fee') bounds.push(['priceMin', 'min'], ['priceMax', 'max']);
    else bounds.push(['feeMax', 'max']);
    bounds.forEach(([key, open]) => {
        const value = filters[key];
        if (value != null && value !== '' && value !== open && Number.isFinite(Number(value))) body[key] = Number(value);
    });
    if (filters.balcony === true) body.balcony = true;
    if (filters.elevator === true) body.elevator = true;
    if (filters.newConstruction === 'only' || filters.newConstruction === 'exclude') {
        body.newConstruction = filters.newConstruction;
    }
    return body;
};

// POST /listings/active/price-histogram (body.field 'fee' for fees); null when it fails (e.g. an older
// backend), which is not cached.
export const fetchPriceHistogram = (body) => {
    const key = JSON.stringify(body);
    if (!histogramCache.has(key)) {
        if (histogramCache.size >= HISTOGRAM_CACHE_MAX) histogramCache.clear();
        const request = apiFetch(`${API_BASE}/listings/active/price-histogram`, {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: key,
        })
            .then((response) => (response.ok ? response.json() : null))
            .then((result) => (Array.isArray(result?.counts) && result.counts.length === body.edges.length + 1 ? result.counts : null))
            .catch(() => null);
        histogramCache.set(key, request);
        request.then((counts) => { if (!counts) histogramCache.delete(key); });
    }
    return histogramCache.get(key);
};

// Handle positions are bar edges (one bar per step: below the first edge, between each pair, at or above
// the last): low 0 = no minimum, high edges.length + 1 = no maximum.
const lowIndexFor = (edges, min) => {
    const value = Number(min);
    if (min === 'min' || !Number.isFinite(value) || value <= 0) return 0;
    const index = edges.findIndex((edge) => edge >= value);
    return index === -1 ? edges.length : index + 1;
};

const highIndexFor = (edges, max) => {
    const value = Number(max);
    if (max === 'max' || !Number.isFinite(value)) return edges.length + 1;
    const index = edges.findIndex((edge) => edge >= value);
    return index === -1 ? edges.length + 1 : index + 1;
};

const formatPrice = (value, swedish) => {
    if (value >= 1000000) {
        const millions = value / 1000000;
        const text = Number.isInteger(millions) ? String(millions) : millions.toFixed(1);
        return `${swedish ? text.replace('.', ',') : text} ${swedish ? 'mkr' : 'M SEK'}`;
    }
    return `${value.toLocaleString(swedish ? 'sv-SE' : 'en-US')} kr`;
};

const formatFee = (value, swedish) => `${value.toLocaleString(swedish ? 'sv-SE' : 'en-US')} kr`;

const describeRange = (low, high, swedish) => {
    const min = low > 0 ? PRICE_EDGES[low - 1] : null;
    const max = high < PRICE_EDGES.length + 1 ? PRICE_EDGES[high - 1] : null;
    if (min == null && max == null) return swedish ? 'Alla' : 'Any';
    if (min == null) return `${swedish ? 'upp till' : 'up to'} ${formatPrice(max, swedish)}`;
    if (max == null) return `${swedish ? 'från' : 'from'} ${formatPrice(min, swedish)}`;
    if (min >= 1000000) {
        // "1 – 4 mkr" rather than "1 mkr – 4 mkr".
        const unit = swedish ? ' mkr' : ' M SEK';
        return `${formatPrice(min, swedish).replace(unit, '')} – ${formatPrice(max, swedish)}`;
    }
    return `${formatPrice(min, swedish)} – ${formatPrice(max, swedish)}`;
};

// A bar per step showing how many homes there are at that value, with handles on the bar edges.
// maxOnly: one handle (the maximum), for the fee. subtle: smaller and quieter, for a secondary filter.
const HistogramRangeBar = ({
    edges, field, min, max, onChange, filters, getHistogram, histogramScope,
    label, describe, handleText, handleLabels, scale, maxOnly = false, subtle = false,
}) => {
    const barCount = edges.length + 1;
    const [counts, setCounts] = useState(() => null);
    const loaderRef = useRef(getHistogram);
    loaderRef.current = getHistogram;
    const filtersKey = JSON.stringify(filters || {});
    const trackRef = useRef(null);
    const dragRef = useRef(null);
    const low = maxOnly ? 0 : lowIndexFor(edges, min);
    const high = Math.max(low + 1, highIndexFor(edges, max));
    const isFull = low === 0 && high === barCount;

    // Recount shortly after the filters stop changing; the old bars stay until the new ones arrive.
    useEffect(() => {
        let cancelled = false;
        const timer = setTimeout(async () => {
            const current = JSON.parse(filtersKey);
            let result = null;
            try {
                result = loaderRef.current ? await loaderRef.current(current, edges, field) : null;
            } catch (error) {
                result = null;
            }
            if (!result) {
                result = await fetchPriceHistogram({
                    edges,
                    ...(field === 'price' ? {} : { field }),
                    ...histogramFilterBody(current, field),
                });
            }
            if (!cancelled) setCounts(result);
        }, RECOUNT_DELAY_MS);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [filtersKey, histogramScope, edges, field]);

    const update = (nextLow, nextHigh) => {
        const lowValue = maxOnly ? 0 : Math.max(0, Math.min(nextLow, barCount - 1));
        const highValue = Math.min(barCount, Math.max(nextHigh, lowValue + 1));
        if (lowValue === low && highValue === high) return;
        onChange(
            lowValue === 0 ? 'min' : edges[lowValue - 1],
            highValue === barCount ? 'max' : edges[highValue - 1],
        );
    };

    // The nearest bar edge under the pointer.
    const edgeAt = (clientX) => {
        const rect = trackRef.current.getBoundingClientRect();
        const ratio = (clientX - rect.left) / Math.max(1, rect.width);
        return Math.min(barCount, Math.max(0, Math.round(ratio * barCount)));
    };

    const moveHandle = (handle, edge) => {
        if (handle === 'low') update(Math.min(edge, high - 1), high);
        else update(low, Math.max(edge, low + 1));
    };

    const handlePointerDown = (event) => {
        if (event.button !== undefined && event.button !== 0) return;
        // On touch screens only the handles drag: a finger on the bars is scrolling the page (touch-action pan-y).
        if (event.pointerType === 'touch' && !event.target?.dataset?.handle) return;
        const edge = edgeAt(event.clientX);
        let handle = event.target?.dataset?.handle;
        if (!handle) handle = !maxOnly && Math.abs(edge - low) <= Math.abs(edge - high) ? 'low' : 'high';
        dragRef.current = handle;
        event.currentTarget.setPointerCapture?.(event.pointerId);
        moveHandle(handle, edge);
    };

    const handlePointerMove = (event) => {
        if (dragRef.current) moveHandle(dragRef.current, edgeAt(event.clientX));
    };

    const endDrag = () => {
        dragRef.current = null;
    };

    const handleKeyDown = (handle) => (event) => {
        const current = handle === 'low' ? low : high;
        let next = null;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') next = current - 1;
        else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') next = current + 1;
        else if (event.key === 'Home') next = handle === 'low' ? 0 : low + 1;
        else if (event.key === 'End') next = handle === 'high' ? barCount : high - 1;
        if (next == null) return;
        event.preventDefault();
        moveHandle(handle, next);
    };

    const peak = counts ? Math.max(1, ...counts) : 1;

    return (
        <div className={`price-range${subtle ? ' price-range--subtle' : ''}`}>
            <div className="score-range-head">
                <span className="score-range-label">{label}</span>
                <span className={`score-range-value${isFull ? ' is-full' : ''}`}>{describe(low, high)}</span>
            </div>
            <div
                className="price-range-track"
                ref={trackRef}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
            >
                <div className={`price-range-bars${counts ? '' : ' is-empty'}`} aria-hidden="true">
                    {Array.from({ length: barCount }, (_, index) => {
                        // Square root, so the few very expensive homes still show as a sliver.
                        const height = counts ? Math.max(4, Math.sqrt(counts[index] / peak) * 100) : 18;
                        return (
                            <span
                                key={index}
                                className={`price-range-bar${index >= low && index < high ? '' : ' is-out'}`}
                                style={{ height: `${height}%` }}
                            />
                        );
                    })}
                </div>
                {(maxOnly ? ['high'] : ['low', 'high']).map((handle) => {
                    const index = handle === 'low' ? low : high;
                    return (
                        <span
                            key={handle}
                            data-handle={handle}
                            className="score-range-handle price-range-handle"
                            style={{ left: `${(index / barCount) * 100}%` }}
                            role="slider"
                            tabIndex={0}
                            aria-label={handleLabels[handle]}
                            aria-valuemin={0}
                            aria-valuemax={barCount}
                            aria-valuenow={index}
                            aria-valuetext={handleText(handle, index)}
                            onKeyDown={handleKeyDown(handle)}
                        />
                    );
                })}
            </div>
            <div className="score-range-scale">
                {scale.map((text, index) => <span key={index}>{text}</span>)}
            </div>
        </div>
    );
};

const PriceRangeBar = ({ priceMin, priceMax, onChange, filters, getPriceHistogram, histogramScope, swedish }) => (
    <HistogramRangeBar
        edges={PRICE_EDGES}
        field="price"
        min={priceMin}
        max={priceMax}
        onChange={onChange}
        filters={filters}
        getHistogram={getPriceHistogram}
        histogramScope={histogramScope}
        label={swedish ? 'Pris' : 'Price'}
        describe={(low, high) => describeRange(low, high, swedish)}
        handleText={(handle, index) => {
            if (handle === 'low') return index === 0 ? (swedish ? 'Ingen lägsta' : 'No minimum') : formatPrice(PRICE_EDGES[index - 1], swedish);
            return index === PRICE_EDGES.length + 1 ? (swedish ? 'Ingen högsta' : 'No maximum') : formatPrice(PRICE_EDGES[index - 1], swedish);
        }}
        handleLabels={swedish
            ? { low: 'Lägsta pris', high: 'Högsta pris' }
            : { low: 'Minimum price', high: 'Maximum price' }}
        scale={['0', `30+ ${swedish ? 'mkr' : 'M'}`]}
    />
);

// Highest monthly fee (feeMax 'max' | kr): one handle, quieter than the price bars above it. Homes
// without a fee (most villas) aren't in the bars.
export const FeeRangeBar = ({ feeMax, onChange, filters, getPriceHistogram, histogramScope, swedish }) => {
    const describeFee = (high) => (high === FEE_EDGES.length + 1
        ? (swedish ? 'Alla' : 'Any')
        : `${swedish ? 'högst' : 'at most'} ${formatFee(FEE_EDGES[high - 1], swedish)}`);
    return (
        <HistogramRangeBar
            edges={FEE_EDGES}
            field="fee"
            max={feeMax}
            maxOnly
            subtle
            onChange={(_min, max) => onChange(max)}
            filters={filters}
            getHistogram={getPriceHistogram}
            histogramScope={histogramScope}
            label={swedish ? 'Avgift per månad' : 'Monthly fee'}
            describe={(_low, high) => describeFee(high)}
            handleText={(_handle, index) => (index === FEE_EDGES.length + 1
                ? (swedish ? 'Ingen högsta' : 'No maximum')
                : formatFee(FEE_EDGES[index - 1], swedish))}
            handleLabels={{ high: swedish ? 'Högsta avgift' : 'Maximum fee' }}
            scale={['0', `15 000+ kr`]}
        />
    );
};

export default PriceRangeBar;
